import type { Node } from 'slate';
import { Element, Text } from 'slate';

import { ORDERED_LIST_STYLES } from '../../extract-paragraphs/docx/list-utils';
import type { ListDefinitions, ParagraphElement, TableElement } from '../../slate-api';
import {
  isLinkElement,
  isParagraphElement,
  isTableCellElement,
  isTableElement,
  isTableRowElement,
  Queries,
} from '../../slate-api';
import { ptToPx } from '../../util';

/**
 * Serializes Aline nodes to an HTML string for the clipboard's `text/html`
 * flavor, so a copied selection can be pasted into other apps (Word, Google
 * Docs, email).
 *
 * Browser-only: uses the global `document`, so it cannot run server-side.
 *
 * Preserves structure for paragraphs, headings (derived from the paragraph
 * style variant), bullet/numbered lists (reconstructed from the flat list
 * paragraphs and the document's list definitions), tables, links, and inline
 * text styling (bold/italic/underline/strikethrough/color/font/size). It does
 * NOT represent vertical cell merges (rowspan) — a merged cell renders as
 * separate `<td>`s.
 *
 * Aline never re-imports this output — internal paste uses the encoded slate
 * fragment also placed on the clipboard — so the HTML only needs to be good
 * enough for other apps to consume.
 *
 * `listDefinitions` (from `Queries.getNumbering(editor).listDefinitions`) is
 * what tells a list paragraph whether it is ordered or unordered; without it,
 * lists fall back to unordered `<ul>`.
 */
export function alineToHtmlForClipboard(
  nodes: Node[],
  listDefinitions: ListDefinitions = {}
): string {
  return serializeBlocks(nodes, listDefinitions);
}

type ListItem = { inner: string; level: number; ordered: boolean };

/**
 * Serializes a sequence of block-level nodes (paragraphs, headings, tables),
 * grouping consecutive list paragraphs into nested `<ul>`/`<ol>`.
 */
function serializeBlocks(blocks: Node[], listDefinitions: ListDefinitions): string {
  const parts: string[] = [];
  let index = 0;

  while (index < blocks.length) {
    const node = blocks[index];
    if (!node) break;

    if (isParagraphElement(node) && getListItemInfo(node, listDefinitions)) {
      const items: ListItem[] = [];
      while (index < blocks.length) {
        const candidate = blocks[index];
        if (!candidate || !isParagraphElement(candidate)) break;
        const info = getListItemInfo(candidate, listDefinitions);
        if (!info) break;
        items.push({ inner: serializeInline(candidate.children), ...info });
        index += 1;
      }
      parts.push(serializeListRun(items));
      continue;
    }

    if (isParagraphElement(node)) {
      parts.push(serializeParagraph(node));
    } else if (isTableElement(node)) {
      parts.push(serializeTable(node, listDefinitions));
    } else if (Text.isText(node) || isLinkElement(node)) {
      parts.push(serializeInline([node]));
    } else if (Element.isElement(node)) {
      parts.push(serializeBlocks(node.children, listDefinitions));
    }
    index += 1;
  }

  return parts.join('');
}

/**
 * Builds nested `<ul>`/`<ol>` from a run of consecutive list paragraphs, using
 * each item's 0-based `level` for nesting depth and `ordered` for the list tag.
 * A deeper item nests inside the previous item's `<li>`; a shallower item pops
 * back up. The list's ordered/unordered tag is fixed when its first item opens
 * it, so a same-level switch between bullet and numbered keeps the first tag.
 */
function serializeListRun(items: ListItem[]): string {
  const root = document.createElement('div');
  const stack: { element: HTMLElement; level: number }[] = [];

  for (const item of items) {
    let top = stack[stack.length - 1];
    while (top && top.level > item.level) {
      stack.pop();
      top = stack[stack.length - 1];
    }

    if (!top || top.level < item.level) {
      const listElement = document.createElement(item.ordered ? 'ol' : 'ul');
      if (top) {
        const parentItem = top.element.lastElementChild ?? top.element;
        parentItem.appendChild(listElement);
      } else {
        root.appendChild(listElement);
      }
      top = { element: listElement, level: item.level };
      stack.push(top);
    }

    const listItem = document.createElement('li');
    listItem.innerHTML = item.inner;
    top.element.appendChild(listItem);
  }

  return root.innerHTML;
}

/**
 * Returns a paragraph's 0-based list level and whether it is an ordered list,
 * or null when the paragraph is not a list item. Ordered/unordered comes from
 * the list definition's marker style; an unknown list falls back to unordered.
 */
function getListItemInfo(
  paragraph: ParagraphElement,
  listDefinitions: ListDefinitions
): { level: number; ordered: boolean } | null {
  const listProps = Queries.renderedParagraphListProps(paragraph);
  if (!listProps) return null;

  const styleName = listDefinitions[listProps.listId]?.[listProps.listLevel]?.style.name;
  return {
    level: listProps.listLevel,
    ordered: styleName ? ORDERED_LIST_STYLES.has(styleName) : false,
  };
}

/** Serializes a non-list paragraph as a heading (`<h1>`–`<h6>`) or `<p>`. */
function serializeParagraph(paragraph: ParagraphElement): string {
  const element = document.createElement(headingTagForVariant(paragraph.variant) ?? 'p');
  element.innerHTML = serializeInline(paragraph.children);
  return element.outerHTML;
}

function headingTagForVariant(variant: ParagraphElement['variant']): string | null {
  const match = String(variant).match(/^style-heading([1-6])$/);
  return match ? `h${match[1]}` : null;
}

function serializeTable(table: TableElement, listDefinitions: ListDefinitions): string {
  const tableElement = document.createElement('table');
  const tbody = document.createElement('tbody');

  for (const row of table.children) {
    if (!isTableRowElement(row)) continue;
    const tableRow = document.createElement('tr');
    for (const cell of row.children) {
      if (!isTableCellElement(cell)) continue;
      const tableCell = document.createElement('td');
      if (cell.columnSpan && cell.columnSpan > 1) {
        tableCell.setAttribute('colspan', String(cell.columnSpan));
      }
      tableCell.innerHTML = serializeBlocks(cell.children, listDefinitions);
      tableRow.appendChild(tableCell);
    }
    tbody.appendChild(tableRow);
  }

  tableElement.appendChild(tbody);
  return tableElement.outerHTML;
}

/** Serializes inline content: text nodes to styled `<span>`s and links to `<a>`s. */
function serializeInline(nodes: Node[]): string {
  return nodes
    .map((node): string => {
      if (Text.isText(node)) {
        const span = document.createElement('span');
        span.textContent = node.text;
        Object.assign(span.style, getStyles(node));

        return span.outerHTML;
      }

      if (isLinkElement(node)) {
        const anchor = document.createElement('a');
        anchor.href = node.href;
        anchor.innerHTML = serializeInline(node.children);
        return anchor.outerHTML;
      }

      if (Element.isElement(node)) return serializeInline(node.children);

      return '';
    })
    .join('');
}

function getStyles(text: Text): Partial<CSSStyleDeclaration> {
  const style: Partial<CSSStyleDeclaration> = {};

  if (text.fontColor) {
    style.color = text.fontColor.color;
  }

  if (text.fontType) {
    style.fontFamily = text.fontType.fontFamily;
  }

  if (text.fontSize) {
    const fontSize = Queries.renderedFontSize(text);

    style.fontSize = `${ptToPx(fontSize)}px`;
  }

  if (text.italic?.active) {
    style.fontStyle = 'italic';
  }

  if (text.bold?.active) {
    style.fontWeight = 'bold';
  }

  if (text.smallCaps?.active) style.fontVariant = 'small-caps';

  if (text.verticalAlign && text.verticalAlign.align !== 'baseline') {
    style.verticalAlign = text.verticalAlign.align.replace('script', '');
  }

  const textDecorations: string[] = [];

  if (text.underline?.active) {
    textDecorations.push('underline');
  }

  if (text.strikethrough?.active) {
    textDecorations.push('line-through');
  }

  if (textDecorations.length > 0) {
    style.textDecoration = textDecorations.join(' ');
  }

  return style;
}
